import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import styles from '@/components/CampaignHeader/CampaignHeader.module.scss';
import DialogSearch from '@/components/DialogSearch/DialogSearch';
import Button from '@/components/UI/Button/Button';
import ConfirmDialog from '@/components/UI/Dialog/ConfirmDialog';
import Dropdown from '@/components/UI/Dropdown/Dropdown';
import InputText from '@/components/UI/InputText/InputText';
import SimpleButton from '@/components/UI/SimpleButton/SimpleButton';
import DialogSkip from '@/containers/CampaignDetail/Summary/DialogSkip/DialogSkip';
import { ERROR, statusCampaign as STATUS } from '@/helpers/constants';
import * as Const from '@/helpers/constants';
import { CR_PERMISSION_ACTIONS } from '@/helpers/crPermissions';
import { isAssignInfoReady, isCampaignInRD, toLanguageObject } from '@/helpers/utils';
import useApiRequest from '@/hooks/useApiRequest';
import useAppLanguage from '@/hooks/useAppLanguage';
import useAuth from '@/hooks/useAuth';
import useCRPermissionMatrix from '@/hooks/useCRPermissionMatrix';
import useGlobalSpinnerContext from '@/hooks/useGlobalSpinner';
import useGoTo from '@/hooks/useGoTo';
import useToastContext from '@/hooks/useToastContext';
import useVisibilityMatrix from '@/hooks/useVisibilityMatrix';
import { getUserInfo } from '@/redux/User/user.reducer';
import campaignServices from '@/services/campaignServices';
import { User, Campaign, CampaignInfoOverview, UserInfo } from '@/types';
import { StatusCampaign, StatusSubCampaign, VisibilityMatrixAction, VisibilityMatrixFeature } from '@/types/Matrix';

interface SummaryButtonProps {
  campaignId?: string;
  reloadCR?: (fullReset?: boolean, crVersion?: number) => void;
  campaignStatus: string;
  campaignVersion: number;
  campaign?: Campaign;
  campaignInfoOverview?: CampaignInfoOverview;
  validateSummaryTranslations?: () => boolean;
  saveCampaign?: () => Promise<void>;
  campaignHasChanges?: boolean;
  skippable?: boolean;
  isVANCR: boolean;
  userInfo: User;
  isOverviewMakeDecisionByCC?: string | '';
  disabled?: boolean;
  reloadOverview?: () => void;
}
const SummaryButton: React.FC<SummaryButtonProps> = (props) => {
  const { campaignId, reloadCR = () => { }, campaignStatus, campaignVersion, campaign, validateSummaryTranslations = () => false,
    saveCampaign = () => null, campaignHasChanges = false, skippable, isVANCR, userInfo, campaignInfoOverview,
    isOverviewMakeDecisionByCC = null, disabled = false, reloadOverview = () => { } } = props;
  const [openDecisionDialog, setOpenDecisionDialog] = useState(false);
  const [openNoChangesCampaign, setOpenNoChangesCampaign] = useState(false);
  const [openSkip, setDialogSkip] = useState(false);
  const [workflow, setWorkflow] = useState();
  const [e2QMBSignature, setE2QMBSignature] = useState({ visible: false, enabled: false, popupOpen: false });
  const [isLoading, setIsLoading] = useState(false);
  const [doormanWarningDialog, setDoormanWarningDialog] = useState(false);
  const { t } = useTranslation();
  const authService = useAuth();
  const lang = useAppLanguage();
  const { addToast } = useToastContext();
  const { canSee } = useVisibilityMatrix();
  const campaignInfo = campaign?.info || campaignInfoOverview;
  const { isButtonDisabled, asStandIn } = useCRPermissionMatrix(campaignInfo?.status!, campaignInfo?.authorization!);
  const { setLoading: setGlobalLoading } = useGlobalSpinnerContext();
  const { goTo } = useGoTo();
  const showSubmitSuccess = () => addToast(t('alert.successfullySent'));
  const [decisionForm, setDecisionForm] = useState({
    decision: campaignStatus === STATUS.IN_REVIEW ? Const.dialogDecisionOptions.SEND_TO_APPROVAL : Const.dialogDecisionOptions.APPROVED,
    comment: '',
  });
  const [decisionHasMandatoryError, setDecisionHasMandatoryError] = useState(false);
  const [decisionHasMinLengthError, setDecisionHasMinLengthError] = useState(false);
  const [openAssignUserDialog, setOpenAssignUserDialog] = useState(false);
  const [plantComments, setPlantComments] = useState({});
  const isCampaignInRDCommittee = isCampaignInRD(campaignStatus);
  const hideRejectOption = isCampaignInRDCommittee || campaignStatus === STATUS.IN_REVIEW || isOverviewMakeDecisionByCC;
  const hideChangeRequestOption = ([STATUS.IN_VAN_CAUSER_APPROVAL, STATUS.IN_CARLINE_CONFIRMATION].includes(campaignStatus) && isVANCR)
    || isOverviewMakeDecisionByCC === Const.statusSubCampaign.IN_PLANT_COORDINATION;
  const isMandatoryComment = Const.dialogDecisionOptions.CHANGE === decisionForm.decision
    || (Const.dialogDecisionOptions.REJECTED === decisionForm.decision && !isVANCR)
    || isOverviewMakeDecisionByCC === Const.statusSubCampaign.IN_PLANT_COORDINATION;
  const isCRAssignedToAuthUser = campaignInfo?.assignedUser?.code === authService.userCode;
  const isStandInUser = userInfo?.standInList?.some((user) => user?.userCode === campaignInfo?.assignedUser?.code);
  const options = {
    ...(campaignStatus === STATUS.IN_REVIEW
      ? toLanguageObject(Const.dialogDecisionOptions.SEND_TO_APPROVAL, 'buttonsHeader.sendToApproval')
      : toLanguageObject(Const.dialogDecisionOptions.APPROVED, 'action.APPROVED')),
    ...(hideChangeRequestOption ? {} : toLanguageObject(Const.dialogDecisionOptions.CHANGE, 'action.CHANGE')),
    ...(hideRejectOption ? {} : toLanguageObject(Const.dialogDecisionOptions.REJECTED, 'action.REJECTED')),
  };
  const approversComments = [
    { title: t('decisionDialog.approverCauserComment'), key: 'commentsApproverCauser' },
    { title: t('decisionDialog.approverInformerComment'), key: 'commentsApproverInformer' },
  ];
  const canSeeCR = (action: VisibilityMatrixAction) => canSee({
    feature: VisibilityMatrixFeature.CAMPAIGN_REQUEST,
    action: action,
    status: campaignStatus as StatusCampaign | StatusSubCampaign, // TODO Missing type on status,
  }) || !isButtonDisabled(action);

  const buildRequestPlantComments = () => Object.keys(plantComments)?.reduce((acc, wkz) => {
    acc[wkz] = { DE: plantComments[wkz], EN: plantComments[wkz] };

    return acc;
  }, {});
  const sendToValidate = async (comment = '') => {
    setIsLoading(true);

    if (!isOverviewMakeDecisionByCC && campaignHasChanges) {
      await saveCampaign();
    }
    (isOverviewMakeDecisionByCC
      ? campaignServices.avanceAllSubCRsOfCRToNextStatus(campaignId, { comment, plantComments: buildRequestPlantComments() }, !!isOverviewMakeDecisionByCC)
      : campaignServices.moveToNextStatus(campaignId, { skipApproval: false, comment }))
      .then(() => {
        showSubmitSuccess();

        if (isOverviewMakeDecisionByCC) {
          reloadOverview();
        } else {
          reloadCR();
        }
      }).catch((error) => {
        if (error.code === Const.errorTypes.campaign_should_close_by_doorman_status) {
          setDoormanWarningDialog(true);
        } else {
          addToast(error.message, ERROR);
        }
      }).finally(() => setIsLoading(false));
  };
  const handleClickSendToApproval = async () => {
    setIsLoading(true);

    if (campaignHasChanges) {
      await saveCampaign();
    }

    if (validateSummaryTranslations()) {
      if (campaignVersion > 1) {
        const isWithoutChangesCR = await campaignServices.getIsCampaignWithoutChanges(campaignId);

        if (isWithoutChangesCR) {
          setOpenNoChangesCampaign(true);
          setIsLoading(false);

          return;
        }
      }

      if (campaignStatus === STATUS.IN_CREATION && campaignVersion > 1) {
        setDialogSkip(true);
      } else {
        sendToValidate();
      }
    } else {
      addToast(t('alert.mandatoryFieldsEmpty'), 'Warning', Const.errorTypes.mandatory_fields_empty);
    }
    setIsLoading(false);
  };

  const validateDecisionComment = (newState) => {
    switch (newState.decision) {
      case Const.dialogDecisionOptions.APPROVED:
      case Const.dialogDecisionOptions.SEND_TO_APPROVAL:
        if (isOverviewMakeDecisionByCC === Const.statusSubCampaign.IN_PLANT_COORDINATION) {
          setDecisionHasMandatoryError(!newState.comment);
          setDecisionHasMinLengthError(newState.comment && newState.comment?.length < 8);
        } else {
          setDecisionHasMandatoryError(false);
        }
        break;
      case Const.dialogDecisionOptions.CHANGE:
      case Const.dialogDecisionOptions.REJECTED:
        setDecisionHasMandatoryError(!newState.comment);
        setDecisionHasMinLengthError(newState.comment && newState.comment?.length < 8);
        break;
      default:
        break;
    }
  };

  const handlePlantCommentChange = (wkz, comment) => {
    const newPlantComments = {
      ...plantComments,
      [wkz]: comment,
    };
    setPlantComments(newPlantComments as any);
  };

  const handleDecisionFormChanges = (label: string, value: string) => {
    const newState = {
      ...decisionForm,
      [label]: value,
    };
    validateDecisionComment(newState);
    setDecisionForm(newState);
  };

  const changeDecision = (comment = '') => {
    (isOverviewMakeDecisionByCC
      ? campaignServices.changeDecisionAllSubCRsOfCR(campaignId, { comment, plantComments: buildRequestPlantComments() })
      : campaignServices.changeDecision(campaignId, { comment }))
      .then(() => {
        showSubmitSuccess();

        if (isOverviewMakeDecisionByCC) {
          reloadOverview();
        } else {
          reloadCR(true, campaignInfo?.version);
          goTo(`/campaign/${campaignId}/${campaignInfo?.version}/summary`);
        }
      }).catch((error) => {
        if (error.code === Const.errorTypes.campaign_should_close_by_doorman_status) {
          setDoormanWarningDialog(true);
        } else {
          addToast(error.message, ERROR);
        }
      });
  };

  const rejectDecision = (comment = '') => {
    campaignServices.rejectDecision(campaignId, {
      comment,
    }).then(() => {
      showSubmitSuccess();
      reloadCR();
    }).catch((error) => {
      if (error.code === Const.errorTypes.campaign_should_close_by_doorman_status) {
        setDoormanWarningDialog(true);
      } else {
        addToast(error.message, ERROR);
      }
    });
  };

  const confirmDecision = () => {
    switch (decisionForm.decision) {
      case Const.dialogDecisionOptions.APPROVED:
      case Const.dialogDecisionOptions.SEND_TO_APPROVAL:
        sendToValidate(decisionForm.comment);
        break;
      case Const.dialogDecisionOptions.CHANGE:
        changeDecision(decisionForm.comment);
        break;
      case Const.dialogDecisionOptions.REJECTED:
        rejectDecision(decisionForm.comment);
        break;
      default:
        break;
    }

    setOpenDecisionDialog(false);
  };

  const confirmKnowledge = () => {
    sendToValidate();
    setE2QMBSignature({ ...e2QMBSignature, popupOpen: false });
  };

  const handleAcceptSendSameCampaign = () => {
    setOpenNoChangesCampaign(false);

    if (campaignStatus === STATUS.IN_CREATION && campaignVersion > 1) {
      setDialogSkip(true);
    } else {
      sendToValidate();
    }
  };

  const showMakeDecision = [
    STATUS.IN_CAUSER_APPROVAL, STATUS.IN_INFORMER_APPROVAL, STATUS.IN_CAUSER_PRE_APPROVAL, STATUS.IN_INFORMER_PRE_APPROVAL,
    STATUS.IN_CAUSER_CHECK, STATUS.IN_INFORMER_CHECK, STATUS.IN_REVIEW, STATUS.IN_VAN_CAUSER_APPROVAL, STATUS.IN_CARLINE_CONFIRMATION,
  ].includes(campaignStatus)
  || (isCampaignInRDCommittee && (isCRAssignedToAuthUser || isStandInUser));

  const showConfirmKnoledge = canSeeCR(VisibilityMatrixAction.CONFIRM_KNOWLEDGE) && e2QMBSignature.visible;

  const assignCR = (selectedUser) => {
    setOpenAssignUserDialog(false);
    const user = selectedUser ? selectedUser[0] : userInfo;
    const data = { code: user.userCode, department: user.department, summary: `${user.lastName}, ${user.firstName}, ${user.department}` };
    campaignServices.putAssignCR(campaignInfo?.id, data, campaignInfo?.type, campaignInfo?.creationDate).then(() => {
      reloadCR();
    }).catch((error) => {
      if (error.code === Const.errorTypes.campaign_should_close_by_doorman_status) {
        setDoormanWarningDialog(true);
      } else {
        addToast(error.message, ERROR);
      }
    });
  };

  const canReassign = !campaignInfo?.assignedUser?.code;

  const checkAssignToDisabled = isButtonDisabled(CR_PERMISSION_ACTIONS.ASSIGN_TO) || !canReassign;
  const checkAssignMeDisabled = isButtonDisabled(CR_PERMISSION_ACTIONS.ASSIGN) || !canReassign;

  const assignInfoReady = isAssignInfoReady(campaignInfo);

  useEffect(() => {
    setGlobalLoading(!assignInfoReady);

    return () => setGlobalLoading(false);
  }, [assignInfoReady]); // eslint-disable-line react-hooks/exhaustive-deps

  const checkE2SignatureActive = useApiRequest(campaignServices.checkE2SignatureActive);

  const isCRAlreadyAssigned = !canReassign && userInfo?.roleSelected === Const.RD_COMMITTEE_COORDINATOR_CR && isCampaignInRDCommittee;

  useEffect(() => {
    if (!isOverviewMakeDecisionByCC) {
      campaignServices.getWorkflow(campaignId)
        .then(setWorkflow)
        .catch((error) => addToast(error.message, ERROR));

      if (canSeeCR(VisibilityMatrixAction.CONFIRM_KNOWLEDGE) && isVANCR) {
        checkE2SignatureActive(campaignId).then((res) => setE2QMBSignature(
          { ...e2QMBSignature, visible: res.scePotential, enabled: res.subCampaignsAdvanced },
        ))
          .catch((error) => { if (error.name !== 'AbortError') addToast(error.message, ERROR); });
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isOverviewMakeDecisionByCC === Const.statusSubCampaign.IN_CONSOLIDATION) {
      setPlantComments(campaignInfoOverview?.subCampaigns?.reduce((acc, subCampaign) => {
        acc[subCampaign.plant] = subCampaign.statusComment;

        return acc;
      }, {}) as any);
    } else if (isOverviewMakeDecisionByCC === Const.statusSubCampaign.IN_PLANT_COORDINATION) {
      campaignServices.getCampaignPlantFeedback(campaignId)
        .then((res) => {
          setPlantComments(res?.plantFeedbackHttpResponses?.reduce((acc, subCR) => {
            acc[subCR.plant] = subCR?.approversInformation?.commentForQmbApprover;

            return acc;
          }, {}) as any);
        })
        .catch((error) => addToast(error.message, ERROR));
      validateDecisionComment(decisionForm);
    }
  }, [campaignInfoOverview]);

  useEffect(() => {
    setDecisionForm({
      decision: campaignStatus === STATUS.IN_REVIEW ? Const.dialogDecisionOptions.SEND_TO_APPROVAL : Const.dialogDecisionOptions.APPROVED,
      comment: '',
    });
  }, [campaignStatus]);

  const standInIsRecipient = campaign?.recipients
    ? Object.values(campaign.recipients).some(
      (recipient) => typeof recipient === 'object' && recipient !== null && 'code' in recipient && (recipient as UserInfo).code === userInfo.userCode,
    )
    : false;

  return (
    <>
      {showMakeDecision
        ? (canSeeCR(VisibilityMatrixAction.MAKE_DECISION) && (
          <Button
            id="buttonMleDecision"
            handleClick={() => setOpenDecisionDialog(true)}
            buttonStyle="RoundButton"
            btnText={t('buttonsHeader.makeDecision')}
            disabled={isLoading || (isButtonDisabled(CR_PERMISSION_ACTIONS.MAKE_DECISION))}
            asStandIn={asStandIn}
          />
        ))
        : (
          canSeeCR(VisibilityMatrixAction.SEND_TO_APPROVAL) && (
            <Button
              id="buttonSubmit"
              handleClick={handleClickSendToApproval}
              buttonStyle="RoundButton"
              btnText={t(`buttonsHeader.${isVANCR ? 'sendToReview' : 'sendToApproval'}`)}
              disabled={isLoading || isButtonDisabled(CR_PERMISSION_ACTIONS.SEND_TO_APPROVAL)}
              asStandIn={!standInIsRecipient && asStandIn}
            />
          )
        )}
      {showConfirmKnoledge && (
        <Button
          id="button-confirm-knowledge"
          handleClick={() => setE2QMBSignature({ ...e2QMBSignature, popupOpen: true })}
          buttonStyle="RoundButton"
          btnText={t('buttonsHeader.confirmKnowledge')}
          disabled={isLoading || isButtonDisabled(CR_PERMISSION_ACTIONS.CONFIRM_KNOWLEDGE) || !e2QMBSignature.enabled}
          asStandIn={asStandIn}
        />
      )}
      {doormanWarningDialog && (
        <ConfirmDialog
          confirm={() => {
            setDoormanWarningDialog(false);
            reloadCR();
          }}
          confirmBtnText={t('dialog.ok')}
          message={t('dialog.doormanMessage')}
        />
      )}
      <div className={styles.ButtonsContainer}>
        {canSeeCR(VisibilityMatrixAction.ASSIGN) && !isCRAlreadyAssigned && (
          <Button
            id="buttonAssignToMeManager"
            handleClick={() => assignCR(null)}
            buttonStyle="RoundButton"
            btnText={t('campaignRequests.assignMe')}
            disabled={isLoading || checkAssignMeDisabled}
            asStandIn={asStandIn}
          />
        )}
        {canSeeCR(VisibilityMatrixAction.ASSIGN_TO) && !isCRAlreadyAssigned && (
          <Button
            id="buttonAssignManager"
            handleClick={() => setOpenAssignUserDialog(true)}
            buttonStyle="RoundButton"
            btnText={t('campaignRequests.assignToATK')}
            disabled={isLoading || checkAssignToDisabled}
            asStandIn={asStandIn}
          />
        )}
      </div>
      {(isOverviewMakeDecisionByCC) && (
        <SimpleButton
          id="btn-approveAll-campaign-subCR-CC"
          color="primary"
          buttonText={t(`buttonsHeader.${isOverviewMakeDecisionByCC === Const.statusSubCampaign.IN_PLANT_COORDINATION ? 'sendToApproval' : 'makeDecision'}`)}
          handleClick={() => setOpenDecisionDialog(true)}
          disabled={disabled}
          asStandIn={asStandIn}
        />
      )}
      {openSkip && (
        <DialogSkip
          close={() => setDialogSkip(false)}
          sendToValidate={sendToValidate}
          workflow={workflow}
          skippable={skippable}
        />
      )}
      {openNoChangesCampaign && (
        <ConfirmDialog
          close={() => setOpenNoChangesCampaign(false)}
          confirm={handleAcceptSendSameCampaign}
          title={t('dialog.warningCampaignWithoutChanges.title')}
          message={t('dialog.warningCampaignWithoutChanges.message')}
          closeBtnText={t('dialog.cancel')}
          confirmBtnText={t('dialog.send')}
        />
      )}
      {openDecisionDialog && (
        <ConfirmDialog
          title={isOverviewMakeDecisionByCC === Const.statusSubCampaign.IN_PLANT_COORDINATION ? t('buttonsHeader.sendToApproval') : t('decisionDialog.title')}
          confirm={confirmDecision}
          reject={() => setOpenDecisionDialog(false)}
          closeBtnText={t('dialog.cancel')}
          confirmBtnText={t('dialog.send')}
          fullWidth
          maxWidth="md"
          confirmDisabled={decisionHasMandatoryError || decisionHasMinLengthError}
        >
          {approversComments?.map((currentComment) => (
            campaign?.recipients[currentComment.key]?.[lang]
              ? (
                <div className={styles.ApproversComments} key={currentComment.key}>
                  <strong>
                    {currentComment.title}
                  </strong>
                  <span>
                    {campaign?.recipients[currentComment.key][lang]}
                  </span>
                </div>
              )
              : ''
          ))}
          <div className={styles.DecisionContainer}>
            <div className={styles.TwoColumns}>
              <Dropdown
                id="decision"
                label={t('decisionDialog.decisionLabel')}
                options={options}
                value={decisionForm.decision}
                handleChange={(event) => { handleDecisionFormChanges('decision', event.target.value); }}
                disabled={Object.keys(options).length === 1}
              />
            </div>
            <div className={styles.OneColumns}>
              <InputText
                invalid={decisionHasMandatoryError || decisionHasMinLengthError}
                isMandatory={isMandatoryComment}
                multiline
                maxLength={1024}
                rows={5}
                id="decision-comment"
                label={t('comment')}
                value={decisionForm.comment}
                handleChange={(event) => { handleDecisionFormChanges('comment', event.target.value); }}
                helperText={[
                  ...((decisionHasMandatoryError) ? [t('validations.required')] : []),
                  ...((decisionHasMinLengthError) ? [t('validations.commentsMinLength')] : []),
                ]}
              />
            </div>
            {(isOverviewMakeDecisionByCC && Object.keys(plantComments)?.length) && (
              <div className={`${styles.OneColumns} ${styles.PlantComments}`}>
                {
                  Object.keys(plantComments || {}).map((wkz, index) => (
                    <InputText
                      key={wkz}
                      multiline
                      rows={4}
                      maxLength={1024}
                      id={`comment-${index}`}
                      label={`${wkz} - ${t('campaign.plantComment')}`}
                      value={plantComments?.[wkz] || ''}
                      handleChange={(event) => { handlePlantCommentChange(wkz, event.target.value); }}
                    />
                  ))
                }
              </div>
            )}
          </div>
        </ConfirmDialog>
      )}
      {e2QMBSignature.popupOpen && (
        <ConfirmDialog
          title={t('confirmKnowledgeDialog.title')}
          message={t('confirmKnowledgeDialog.message')}
          confirm={confirmKnowledge}
          reject={() => setE2QMBSignature({ ...e2QMBSignature, popupOpen: false })}
          closeBtnText={t('dialog.cancel')}
          confirmBtnText={t('dialog.send')}
        />
      )}
      {openAssignUserDialog && (
        <DialogSearch
          role={[(isVANCR ? Const.VAN_COMMITTEE_COORDINATOR : Const.RD_COMMITTEE_COORDINATOR).toUpperCase()]}
          title={t(`campaignRequests.${isVANCR ? 'assignToATK' : 'rdCommittee'}`)}
          close={() => setOpenAssignUserDialog(false)}
          updateOptionSelected={(user) => assignCR(user)}
          isRemote={false}
        />
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  userInfo: getUserInfo(state),
});

export default connect(mapStateToProps)(SummaryButton);
